import { useNavigate } from 'react-router-dom'
import { Bell, CreditCard, AlertTriangle, Server, ShieldCheck, Receipt, Check } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '@shared/components/ui/popover'
import { Button } from '@shared/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@shared/components/ui/tooltip'
import { useNotificationStore } from '@shared/stores/notification.store'
import type { Notification, NotificationType } from '@shared/types/notification.types'
import { cn } from '@shared/lib/utils'

const typeIcons: Record<NotificationType, typeof CreditCard> = {
  payment: CreditCard,
  dispute: AlertTriangle,
  system: Server,
  compliance: ShieldCheck,
  billing: Receipt,
}

function formatRelativeTime(date: Date): string {
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60_000)
  const diffHours = Math.floor(diffMs / 3_600_000)
  const diffDays = Math.floor(diffMs / 86_400_000)

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays === 1) return 'Yesterday'
  return `${diffDays}d ago`
}

function getDateGroup(date: Date): string {
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffDays = Math.floor(diffMs / 86_400_000)

  if (diffDays === 0) return 'Today'
  if (diffDays === 1) return 'Yesterday'
  return 'Earlier'
}

function groupNotifications(notifications: Notification[]) {
  const groups = new Map<string, Notification[]>()
  const order = ['Today', 'Yesterday', 'Earlier']

  for (const n of notifications) {
    const group = getDateGroup(n.timestamp)
    if (!groups.has(group)) groups.set(group, [])
    groups.get(group)!.push(n)
  }

  return order
    .filter((g) => groups.has(g))
    .map((g) => ({ label: g, items: groups.get(g)! }))
}

function NotificationItem({ notification, onAction }: { notification: Notification; onAction: (n: Notification) => void }) {
  const Icon = typeIcons[notification.type]

  return (
    <button
      onClick={() => onAction(notification)}
      className={cn(
        'w-full flex items-start gap-3 px-3 py-2.5 text-left rounded-md transition-colors hover:bg-muted/50',
        !notification.read && 'bg-muted/30'
      )}
    >
      <div className="mt-0.5 shrink-0">
        <Icon className="h-4 w-4 text-muted-foreground" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className={cn('text-sm truncate', !notification.read && 'font-medium')}>
            {notification.title}
          </span>
          {!notification.read && (
            <span className="h-1.5 w-1.5 rounded-full bg-blue-500 shrink-0" />
          )}
        </div>
        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
          {notification.description}
        </p>
        <span className="text-[11px] text-muted-foreground/60 mt-1 block">
          {formatRelativeTime(notification.timestamp)}
        </span>
      </div>
    </button>
  )
}

export function TopBarNotifications() {
  const navigate = useNavigate()
  const notifications = useNotificationStore((s) => s.notifications)
  const unreadCount = useNotificationStore((s) => s.unreadCount)
  const markAsRead = useNotificationStore((s) => s.markAsRead)
  const markAllAsRead = useNotificationStore((s) => s.markAllAsRead)

  const groups = groupNotifications(notifications)

  const handleAction = (notification: Notification) => {
    markAsRead(notification.id)
    if (notification.actionUrl) {
      navigate(notification.actionUrl)
    }
  }

  return (
    <Popover>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button variant="ghost" size="icon" className="relative h-8 w-8">
              <Bell className="h-4 w-4" />
              {unreadCount > 0 && (
                <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-destructive" />
              )}
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent>Notifications</TooltipContent>
      </Tooltip>

      <PopoverContent align="end" className="w-[360px] p-0">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <h3 className="text-sm font-semibold">Notifications</h3>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-auto py-1 px-2 text-xs text-muted-foreground hover:text-foreground"
              onClick={markAllAsRead}
            >
              <Check className="h-3 w-3 mr-1" />
              Mark all as read
            </Button>
          )}
        </div>

        <div className="max-h-[400px] overflow-y-auto">
          {groups.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">
              No notifications
            </div>
          ) : (
            groups.map((group) => (
              <div key={group.label}>
                <div className="px-4 py-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground/60">
                  {group.label}
                </div>
                <div className="px-1">
                  {group.items.map((n) => (
                    <NotificationItem key={n.id} notification={n} onAction={handleAction} />
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
